﻿import {
    MockingbirdServicesWorkSharedV1ContractsEstimatedSalesAllocationContract as EstimatedSalesAllocationContract,
    MockingbirdServicesWorkSharedV1ContractsSalesTargetContract as SalesTargetContract,
} from 'ApiClient/work'
import { EditionSearchDocument } from 'Contact/api/SearchApi'
import { claimTypesEnum } from 'User/utils/claims'
import { Operation } from 'fast-json-patch'
import { DateTime } from 'luxon'

import { Authentication } from 'Common/auth/types'
import { ListAsset } from 'Common/components/assets/AssetGridList'
import { servicesAsync } from 'Common/environment/environment'
import { DistributionRule, SearchResultItem } from 'Common/types'
import { fetchDelete, fetchGet, fetchPatch, fetchPost, fetchPut } from 'Common/utils/query/request'
import { getMyPublishingHouseIds } from 'Common/utils/user/auth'

const getContactsFilter = (contactIds: readonly (string | number)[]) =>
    contactIds.map((contactId) => `contactIds/any(t: t eq '${contactId}')`).join(' or ')

const getPublishingHouseFilter = (publishinghouseIds: readonly (string | number)[]) =>
    publishinghouseIds
        .map((publishingHouseId) => `publishingHouse eq '${publishingHouseId}'`)
        .join(' or ')

const baseUrl = servicesAsync.work('')

interface EditionPatch {
    id: number
    workId: number
    patchSet: Operation[]
}

interface EditionBookClubPatch {
    id: number
    editionId: number
    workId: number
    patchSet: Operation[]
}

interface EditionOtherRequest {
    id: number
    workId: number
}

interface EditionDateRequest {
    id: number
    workId: number
    data: any
}

interface EditionAssetRequest {
    id: number
    workId: number
}

interface EditionAssetPostRequest {
    id: number
    workId: number
    asset: any
}

interface EditionAssetDeleteRequest {
    id: number
    workId: number
    assetId: number
}

interface EditionBookClubDeleteRequest {
    id: number
    workId: number
    bookClubId: number
}

interface MoveEditonRequest {
    sourceWorkId: number
    destinationWorkId: number
    edtionId: number
}

interface EditionAssetPostRequest {
    id: number
    asset: any
}

interface EditionBookClubPostRequest {
    id: number
    workId: number
    bookClub: any
}

interface EditionProductionPlanEntryRequest {
    id: number
    workId: number
    entryId?: string
    data?: any
}

interface EditionHistoryRequest {
    id: number | null
    workId: number | null
    field: string | null
}

interface EditionCopyRequest {
    workId: number
    originEditionId: number
    sharing: string[]
    data: any
}

interface EditionCopyTechnicalProductionRequest {
    id: number
    workId: number
    fromProductionId: number
}

interface ProductionDistributionRuleRequest {
    id: number
    workId: number
    data: DistributionRule[]
}

interface EditionCostsRequest {
    workId: number
    editionId: number
    printId: number
    editionCosts?: { items?: unknown[] }
}

interface EditionSalesTargetRequest {
    workId: number
    editionId: number
    salesTarget?: SalesTargetContract
}

export type EstimatedSales = {
    totalEstimatedSales?: number
    estimatedSalesAllocations?: EstimatedSalesAllocationContract[] | null
}

export const patchEdition = async (editionPatch: EditionPatch) => {
    return await fetchPatch(
        `${await baseUrl}/v1/works/${editionPatch.workId}/productions/${editionPatch.id}`,
        JSON.stringify(editionPatch.patchSet),
    )
}

export const getEdition = async (id: number) => {
    return await fetchGet(`${await baseUrl}/v2/editions/${id}`)
}

export const editionAssetsGet = async (request: EditionAssetRequest) => {
    const result = await fetchGet(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/assets`,
    )
    return result as ListAsset[]
}

export const editionAssetPost = async (request: EditionAssetPostRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/assets`,
        JSON.stringify(request.asset),
    )
}

export const editionAssetDelete = async (request: EditionAssetDeleteRequest) => {
    return await fetchDelete(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/assets/${request.assetId}`,
    )
}

export const editionTextsGet = async (request: EditionOtherRequest) => {
    const response = await fetchGet(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/texts`,
    )

    try {
        // "Repair" old text with newlines not in new WYSIWYG-format with fallback
        response?.texts?.forEach((x: { text: string }) => {
            if (!x.text.includes('<p>')) {
                // Inject p-tag and replace line breaks with break-tag
                x.text = `<p>${x.text.replace(/\n/g, '<br />')}</p>`
            }
        })
        return response
    } catch {
        return response
    }
}

export const patchEditionTexts = async (editionPatch: EditionPatch) => {
    return await fetchPatch(
        `${await baseUrl}/v1/works/${editionPatch.workId}/productions/${editionPatch.id}/texts`,
        JSON.stringify(editionPatch.patchSet),
    )
}

export const getProductionDetailOptions = async () => {
    return await fetchGet(`${await baseUrl}/v1/settings/productiondetailoptions`)
}

export const currenciesGet = async () => {
    return await fetchGet(`${await baseUrl}/v1/settings/currencies`)
}

export const deforestationRegulationStatusGet = async () => {
    return await fetchGet(`${await baseUrl}/v1/settings/deforestationregulationstatuses`)
}

export const postMoveEdition = async (request: MoveEditonRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.sourceWorkId}/moveproductions`,
        JSON.stringify({
            destinationworkid: request.destinationWorkId,
            productionid: request.edtionId,
        }),
    )
}

export const productionsCurrentGet = async (auth: Authentication) => {
    const today = DateTime.utc().startOf('day').toISO()
    const publishingHousesIds = getMyPublishingHouseIds(auth, claimTypesEnum.enum.opus_work)
    const filter = getPublishingHouseFilter(publishingHousesIds)

    const response = await fetchGet(
        `${await baseUrl}/v2/search/productions?q=&limit=500&$orderBy=publishingDate&$filter=(publishingDate ge ${today}) and (${encodeURIComponent(filter)})&$select=id,workId,title,seasonName,publishingDate,bindingCodeName`,
    )

    return response.results as SearchResultItem<EditionSearchDocument>[]
}

export const productionsMyGet = async (auth: Authentication) => {
    const today = DateTime.utc().startOf('day').toISO()
    const contactIds = auth.data?.opus_contact_id ?? []
    const filter = getContactsFilter(contactIds)

    const response = await fetchGet(
        `${await baseUrl}/v2/search/productions?q=&limit=500&$orderBy=publishingDate&$filter=(publishingDate ge ${today}) and (${encodeURIComponent(filter)})&$select=id,workId,title,seasonName,publishingDate,bindingCodeName`,
    )

    return response.results as SearchResultItem<EditionSearchDocument>[]
}

export const editionBookClubGet = async (request: EditionOtherRequest) => {
    return await fetchGet(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/bookclubs`,
    )
}

export const editionBookClubPatch = async (patch: EditionBookClubPatch) => {
    return await fetchPatch(
        `${await baseUrl}/v1/works/${patch.workId}/productions/${patch.editionId}/bookclubs/${patch.id}`,
        JSON.stringify(patch.patchSet),
    )
}

export const editionBookClubPost = async (request: EditionBookClubPostRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/bookclubs`,
        JSON.stringify(request.bookClub),
    )
}

export const editionBookClubDelete = async (request: EditionBookClubDeleteRequest) => {
    return await fetchDelete(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/bookclubs/${request.bookClubId}`,
    )
}

export const EditionErpPost = async (request: EditionOtherRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/connectToERP`,
        undefined,
    )
}

export const editionChangeDatesPost = async (request: EditionDateRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/changedates`,
        JSON.stringify(request.data),
    )
}

export const editionDeleteReviewDate = async (request: EditionOtherRequest) => {
    return await fetchDelete(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/reviewDate`,
    )
}

export const editionProductionPlanPackagePost = async (request: EditionDateRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/activeprint/timePlan/addpackage`,
        JSON.stringify(request.data),
    )
}

export const editionProductionPlanEntryPut = async (request: EditionProductionPlanEntryRequest) => {
    return await fetchPut(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/activeprint/timePlan/entries/${request.entryId}`,
        JSON.stringify(request.data),
    )
}

export const editionProductionPlanEntryPost = async (
    request: EditionProductionPlanEntryRequest,
) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/activeprint/timePlan/entries`,
        JSON.stringify(request.data),
    )
}

export const editionProductionPlanEntryDelete = async (
    request: EditionProductionPlanEntryRequest,
) => {
    return await fetchDelete(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/activeprint/timePlan/entries/${request.entryId}`,
    )
}

export const editionCancelPost = async (request: EditionOtherRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/cancel`,
        undefined,
    )
}

export const editionActivatePost = async (request: EditionOtherRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/activate`,
        undefined,
    )
}

export const editionIsbnPost = async (request: EditionDateRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/setisbn`,
        JSON.stringify(request.data),
    )
}

export const editionBindingCodePost = async (request: EditionDateRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/changebindingcode`,
        JSON.stringify(request.data),
    )
}

export const editionPublishPost = async (request: EditionOtherRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/publish`,
        undefined,
    )
}

export const editionUnpublishPost = async (request: EditionOtherRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/unpublish`,
        undefined,
    )
}

export const editionCreateReprintPost = async (request: EditionDateRequest) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/newprint`,
        JSON.stringify(request.data),
    )
}

export const editionFieldHistoryGet = async (request: EditionHistoryRequest) => {
    const field = request.field ? `?field=${request.field}` : ''
    if (request.workId === null && request.id === null) {
        return null
    }
    return await fetchGet(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/history${field}`,
    )
}

export const editionCopyPost = async (request: EditionCopyRequest) => {
    return await fetchPost(
        [
            `${await baseUrl}/v1/works/${request.workId}/productions/copy?originProductionId=${request.originEditionId}`,
            ...request.sharing.map((share) => `${share}=true`),
        ].join('&'),
        JSON.stringify(request.data),
    )
}

export const editionCopyTechnicalProductionPost = async (
    request: EditionCopyTechnicalProductionRequest,
) => {
    return await fetchPost(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/copytechnicalproduction`,
        JSON.stringify({ fromId: request.fromProductionId }),
    )
}

export const putProductionDistributionRules = async (
    request: ProductionDistributionRuleRequest,
) => {
    return await fetchPut(
        `${await baseUrl}/v1/works/${request.workId}/productions/${request.id}/exportrules`,
        JSON.stringify(request.data),
    )
}

export const editionCostsGet = async (editionCostRequest: EditionCostsRequest) => {
    return await fetchGet(
        `${await baseUrl}/v1/works/${editionCostRequest.workId}/productions/${editionCostRequest.editionId}/prints/${editionCostRequest.printId}/costs`,
    )
}

export const editionCostsPut = async (editionCostRequest: EditionCostsRequest) => {
    return await fetchPut(
        `${await baseUrl}/v1/works/${editionCostRequest.workId}/productions/${editionCostRequest.editionId}/prints/${editionCostRequest.printId}/costs`,
        JSON.stringify(editionCostRequest.editionCosts),
    )
}

export const editionSalesTargetGet = async (
    editionCostRequest: EditionSalesTargetRequest,
): Promise<SalesTargetContract> => {
    return await fetchGet(
        `${await baseUrl}/v1/works/${editionCostRequest.workId}/productions/${editionCostRequest.editionId}/salestarget`,
    )
}

export const editionSalesTargetPut = async (editionCostRequest: EditionSalesTargetRequest) => {
    return await fetchPut(
        `${await baseUrl}/v1/works/${editionCostRequest.workId}/productions/${editionCostRequest.editionId}/salestarget`,
        JSON.stringify(editionCostRequest.salesTarget),
    )
}

export const editionEstimatedSalesGet = async (
    editionCostRequest: EditionSalesTargetRequest,
): Promise<EstimatedSales> => {
    return await fetchGet(
        `${await baseUrl}/v1/works/${editionCostRequest.workId}/productions/${editionCostRequest.editionId}/estimatedsales`,
    )
}

export const refreshPreliminaryCostPrice = async (editionId: number) => {
    return await fetchPost(
        `${await baseUrl}/v2/editions/${editionId}/refreshPreliminaryCostPrice`,
        undefined,
    )
}
